import type { ModelsTierName } from "@app/lib/api/assistant/token_pricing/tiers";
import {
  STATIC_MODEL_SUPPORTED_REASONING_EFFORTS,
  STATIC_MODEL_TIERS,
} from "@app/lib/api/assistant/token_pricing/tiers";
import {
  brandDefaultModelConfig,
  brandDefaultModelReasoningEffort,
} from "@app/lib/brand";
import type { EnabledModelConfigurationType } from "@app/types/api/assistant/models";
import type { ModelStreamIdType } from "@app/types/assistant/models/auto";
import {
  AUTO_COMPLEX_MODEL_ID,
  AUTO_FAST_MODEL_ID,
  AUTO_MODEL_ID,
  isModelStreamId,
} from "@app/types/assistant/models/auto";
import { isStaticModelId } from "@app/types/assistant/models/models";
import type {
  ModelConfigurationType,
  ModelIdType,
  ModelSelectionType,
  ReasoningEffort,
} from "@app/types/assistant/models/types";
import { getAvailableReasoningEfforts } from "@app/types/assistant/models/types";
import { assertNeverAndIgnore } from "@app/types/shared/utils/assert_never";
import capitalize from "lodash/capitalize";

// Shown when a whole-premium model row or a premium reasoning-effort stop is
// locked because the workspace is on a legacy (non usage-based) plan.
export const PREMIUM_MODEL_LOCKED_TOOLTIP =
  "Premium models are available via this picker on the new usage-based plans. " +
  "Contact your administrator to upgrade and access these models.";

// Shown when a model row is locked because the model's tier is not enabled for
// the workspace's current model-tier ceiling (independent of the plan: this
// applies even on usage-based plans whose tier grants stop below the model).
const MODEL_TIER_LOCKED_TOOLTIP =
  "You don't have access to this model tier. " +
  "Contact your administrator to get access.";

// The three primary picks of the model picker. Each tier is backed by a
// meta-model that is resolved to a concrete model at message-send time:
//   - Fast     -> auto_fast (curated pool of small, cheap models)
//   - Standard -> auto       (Memoair picks any available model — the old "Auto")
//   - Complex  -> auto_complex  (curated pool of powerful models)
export type ModelTierId = "fast" | "standard" | "complex";

interface ModelTierDefinition {
  id: ModelTierId;
  metaModelId: ModelStreamIdType;
  name: string;
  // Short right-aligned blurb shown next to the tier name.
  description: string;
}

export const MODEL_TIERS: ModelTierDefinition[] = [
  {
    id: "fast",
    metaModelId: AUTO_FAST_MODEL_ID,
    name: "Fast",
    description: "Quick, low cost",
  },
  {
    id: "standard",
    metaModelId: AUTO_MODEL_ID,
    name: "Standard",
    description: "Best for most",
  },
  {
    id: "complex",
    metaModelId: AUTO_COMPLEX_MODEL_ID,
    name: "Complex",
    description: "Slower, most capable",
  },
];

const TIER_BY_META_MODEL_ID: Record<ModelStreamIdType, ModelTierId> = {
  [AUTO_FAST_MODEL_ID]: "fast",
  [AUTO_MODEL_ID]: "standard",
  [AUTO_COMPLEX_MODEL_ID]: "complex",
};

export function getModelTier(tierId: ModelTierId): ModelTierDefinition {
  // MODEL_TIERS is exhaustive over ModelTierId, so a match is guaranteed; the
  // fallback to the "standard" tier is dead today and only guards against a
  // future tier being removed from the list.
  return (
    MODEL_TIERS.find((tier) => tier.id === tierId) ??
    MODEL_TIERS.find((tier) => tier.id === "standard") ??
    MODEL_TIERS[0]
  );
}

const PREMIUM_MODEL_TIER_IDS: ModelTierId[] = ["complex"];

export function isTierLocked(
  tierId: ModelTierId,
  { lockPremiumEfforts }: { lockPremiumEfforts: boolean }
): boolean {
  return lockPremiumEfforts && PREMIUM_MODEL_TIER_IDS.includes(tierId);
}

export function getUnlockedAutoTier(
  preferred: ModelTierId | undefined,
  { lockPremiumEfforts }: { lockPremiumEfforts: boolean }
): ModelTierId | null {
  const candidates: ModelTierId[] = [];
  if (preferred) {
    candidates.push(preferred);
  }
  for (const tier of MODEL_TIERS) {
    if (!candidates.includes(tier.id)) {
      candidates.push(tier.id);
    }
  }
  return (
    candidates.find((id) => !isTierLocked(id, { lockPremiumEfforts })) ?? null
  );
}

// Per reasoning-effort blurbs surfaced in the effort slider tooltip.
const REASONING_EFFORT_INFO: Record<ReasoningEffort, string> = {
  none: "No additional reasoning, for the fastest responses.",
  light: "Light reasoning effort, faster responses.",
  medium: "Medium reasoning effort, balancing speed and quality.",
  high: "High reasoning effort, longer wait times but higher quality.",
};

export function getReasoningEffortLabel(effort: ReasoningEffort): string {
  return effort === "none" ? "None" : capitalize(effort);
}

// What the picker is currently showing, decoupled from the payload we send:
// a tier or a concrete model + reasoning effort.
export type SelectionDisplay =
  | { kind: "tier"; tierId: ModelTierId }
  | { kind: "model"; model: ModelConfigurationType; effort: ReasoningEffort };

// A resolved picker selection. `toSend` is the explicit model or tier payload
// except when no workspace default is available, in which case it stays
// undefined so the backend keeps its existing fallback.
export interface Selection {
  display: SelectionDisplay;
  toSend: ModelSelectionType | undefined;
}

export type EffortLockReason = "unsupported" | "premium" | "model_tier";

// One stop of the reasoning-effort slider. A stop is `locked` when the level is
// not selectable; `lockedReason` says why.
export interface EffortStop {
  effort: ReasoningEffort;
  locked: boolean;
  lockedReason?: EffortLockReason;
}

// The reasoning-effort slider always presents these three canonical levels so
// its shape stays consistent across models. "none" is not a level here: it
// means "no reasoning" and is never a selectable slider position.
const SLIDER_EFFORTS: ReasoningEffort[] = ["light", "medium", "high"];

export function buildTierSelection(tierId: ModelTierId): ModelSelectionType {
  const { metaModelId } = getModelTier(tierId);
  return {
    providerId: metaModelId,
    modelId: metaModelId,
    reasoningEffort: "none",
  };
}

export function buildModelSelection(
  model: ModelConfigurationType,
  effort: ReasoningEffort
): ModelSelectionType {
  return {
    providerId: model.providerId,
    modelId: model.modelId,
    reasoningEffort: effort,
  };
}

export function getModelKey(providerId: string, modelId: string): string {
  return `${providerId}/${modelId}`;
}

export function isModelSelection(
  model: ModelConfigurationType,
  display: SelectionDisplay
): boolean {
  return (
    display.kind === "model" &&
    display.model.providerId === model.providerId &&
    display.model.modelId === model.modelId
  );
}

export function isTierDisplayed(
  tierId: ModelTierId,
  display: SelectionDisplay
): boolean {
  return display.kind === "tier" && display.tierId === tierId;
}

// Display equality ignoring reasoning effort: two model displays for the same
// model are "the same" regardless of effort. Used to highlight the selected row
// and to mark the default, where effort is surfaced by the slider instead.
function isSameDisplay(a: SelectionDisplay, b: SelectionDisplay): boolean {
  if (a.kind === "tier" && b.kind === "tier") {
    return a.tierId === b.tierId;
  }
  if (a.kind === "model" && b.kind === "model") {
    return (
      a.model.providerId === b.model.providerId &&
      a.model.modelId === b.model.modelId
    );
  }
  return false;
}

// Full selection equality, effort included. Used to decide whether the active
// selection is exactly the agent default (so no override needs to be kept, and
// the revert affordance is hidden).
export function isSameSelection(
  a: SelectionDisplay,
  b: SelectionDisplay
): boolean {
  if (a.kind === "model" && b.kind === "model") {
    return isSameDisplay(a, b) && a.effort === b.effort;
  }
  return isSameDisplay(a, b);
}

interface LockPremiumOptions {
  lockPremiumEfforts?: boolean;
}

// Client-safe mirror of `ModelsTierResource.getTierForModel`
export function getModelEffortTier(
  modelId: ModelIdType,
  effort: ReasoningEffort
): ModelsTierName | null {
  if (!isStaticModelId(modelId)) {
    return "premium";
  }
  return STATIC_MODEL_TIERS[modelId][effort] ?? null;
}

function modelSupportsEffortStatically(
  modelId: ModelIdType,
  effort: ReasoningEffort
): boolean {
  if (!isStaticModelId(modelId)) {
    return false;
  }
  return STATIC_MODEL_SUPPORTED_REASONING_EFFORTS[modelId][effort] === true;
}

// The single authority for whether a reasoning-effort level is selectable.
export function getEffortStops(
  enabledModel: ModelConfigurationType,
  { lockPremiumEfforts = false }: LockPremiumOptions = {}
): EffortStop[] {
  const allowed = new Set(
    getAvailableReasoningEfforts(enabledModel.supportedReasoningEfforts)
  );

  return SLIDER_EFFORTS.map((effort) => {
    if (!allowed.has(effort)) {
      return {
        effort,
        locked: true,
        lockedReason: modelSupportsEffortStatically(
          enabledModel.modelId,
          effort
        )
          ? "model_tier"
          : "unsupported",
      };
    }
    if (
      lockPremiumEfforts &&
      getModelEffortTier(enabledModel.modelId, effort) === "premium"
    ) {
      return { effort, locked: true, lockedReason: "premium" };
    }
    return { effort, locked: false };
  });
}

// The reasoning effort to use when a model is freshly selected: its default when
// that is allowed, otherwise the first unlocked stop.
export function getInitialEffort(
  enabledModel: ModelConfigurationType,
  { lockPremiumEfforts = false }: LockPremiumOptions = {}
): ReasoningEffort {
  const stops = getEffortStops(enabledModel, { lockPremiumEfforts });
  const preferred = stops.find(
    (stop) =>
      stop.effort === enabledModel.defaultReasoningEffort && !stop.locked
  );
  if (preferred) {
    return preferred.effort;
  }
  return stops.find((stop) => !stop.locked)?.effort ?? "none";
}

function isReasoningModel(modelId: ModelIdType): boolean {
  if (!isStaticModelId(modelId)) {
    return false;
  }
  const support = STATIC_MODEL_SUPPORTED_REASONING_EFFORTS[modelId];
  return SLIDER_EFFORTS.some((effort) => support[effort]);
}

// Whether a whole model row must be locked
export function hasEffortControl(
  model: ModelConfigurationType,
  effort: ReasoningEffort,
  { lockPremiumEfforts }: { lockPremiumEfforts: boolean }
): boolean {
  return (
    effort !== "none" &&
    getEffortStops(model, { lockPremiumEfforts }).some((stop) => !stop.locked)
  );
}

export function isPremiumModel(
  enabledModel: ModelConfigurationType,
  { lockPremiumEfforts }: { lockPremiumEfforts: boolean }
): boolean {
  const stops = getEffortStops(enabledModel, { lockPremiumEfforts });
  const hasUsableSliderEffort = stops.some((stop) => !stop.locked);

  if (isReasoningModel(enabledModel.modelId) && !hasUsableSliderEffort) {
    return true;
  }

  if (!lockPremiumEfforts) {
    return false;
  }
  const supportedSlider = stops.filter(
    (stop) => stop.lockedReason !== "unsupported"
  );
  if (supportedSlider.length > 0) {
    return supportedSlider.every((stop) => stop.lockedReason === "premium");
  }
  return getModelEffortTier(enabledModel.modelId, "none") === "premium";
}

export type ModelLockReason = "premium" | "model_tier";

export function getModelLockReason(
  enabledModel: ModelConfigurationType,
  { lockPremiumEfforts }: { lockPremiumEfforts: boolean }
): ModelLockReason | null {
  if (!isPremiumModel(enabledModel, { lockPremiumEfforts })) {
    return null;
  }
  return lockPremiumEfforts ? "premium" : "model_tier";
}

export function getModelLockTooltip(reason: ModelLockReason): string {
  switch (reason) {
    case "premium":
      return PREMIUM_MODEL_LOCKED_TOOLTIP;
    case "model_tier":
      return MODEL_TIER_LOCKED_TOOLTIP;
    default:
      assertNeverAndIgnore(reason);
      return "";
  }
}

export function getEffortStopTooltip(stop: EffortStop): string {
  if (stop.locked) {
    switch (stop.lockedReason) {
      case "premium":
        return PREMIUM_MODEL_LOCKED_TOOLTIP;
      case "model_tier":
        return MODEL_TIER_LOCKED_TOOLTIP;
      case "unsupported":
      case undefined:
        break;
      default:
        assertNeverAndIgnore(stop.lockedReason);
    }
  }
  return REASONING_EFFORT_INFO[stop.effort];
}

function findAvailableModel(
  models: EnabledModelConfigurationType[],
  selection: { providerId: string; modelId: string }
): EnabledModelConfigurationType | undefined {
  return models.find(
    (m) =>
      m.isSelectable &&
      m.providerId === selection.providerId &&
      m.modelId === selection.modelId
  );
}

function resolveRequestedSelection(
  models: EnabledModelConfigurationType[],
  selection: ModelSelectionType | null | undefined
): Selection | null {
  if (!selection) {
    return null;
  }
  if (isModelStreamId(selection.modelId)) {
    const tierId = TIER_BY_META_MODEL_ID[selection.modelId];
    return {
      display: { kind: "tier", tierId },
      toSend: buildTierSelection(tierId),
    };
  }
  const model = findAvailableModel(models, selection);
  if (!model) {
    return null;
  }
  const effort = selection.reasoningEffort ?? getInitialEffort(model);
  return {
    display: { kind: "model", model, effort },
    toSend: buildModelSelection(model, effort),
  };
}

function resolveWorkspaceDefault(
  defaultModel: EnabledModelConfigurationType | null
): Selection {
  if (!defaultModel) {
    return {
      display: { kind: "tier", tierId: "standard" },
      toSend: undefined,
    };
  }
  if (isModelStreamId(defaultModel.modelId)) {
    const tierId = TIER_BY_META_MODEL_ID[defaultModel.modelId];
    return {
      display: { kind: "tier", tierId },
      toSend: buildTierSelection(tierId),
    };
  }
  const effort =
    defaultModel.modelId === brandDefaultModelConfig.modelId
      ? brandDefaultModelReasoningEffort
      : getInitialEffort(defaultModel);
  return {
    display: { kind: "model", model: defaultModel, effort },
    toSend: buildModelSelection(defaultModel, effort),
  };
}

export function stickyAfterConversationChange(
  previousConversationId: string | null,
  nextConversationId: string | null,
  sticky: ModelSelectionType | undefined,
  persisted: ModelSelectionType | undefined
): ModelSelectionType | undefined {
  if (previousConversationId === nextConversationId) {
    return sticky;
  }
  if (previousConversationId === null && nextConversationId !== null) {
    return sticky;
  }
  if (nextConversationId === null) {
    return persisted;
  }
  return undefined;
}

// The selection to show, in precedence order: last-requested (per-conversation)
// > session sticky > workspace default. The workspace default is returned
// separately so callers can mark it "(Default)" and offer a revert affordance.
export function resolveShownSelection({
  lastRequestedModel,
  sessionSticky,
  models,
  defaultModel,
}: {
  lastRequestedModel: ModelSelectionType | null;
  sessionSticky?: ModelSelectionType | null;
  models: EnabledModelConfigurationType[];
  defaultModel: EnabledModelConfigurationType | null;
}): { shown: Selection; workspaceDefault: Selection } {
  const workspaceDefault = resolveWorkspaceDefault(defaultModel);
  const shown =
    resolveRequestedSelection(models, lastRequestedModel) ??
    resolveRequestedSelection(models, sessionSticky) ??
    workspaceDefault;
  return { shown, workspaceDefault };
}
